import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { postulacionService } from '../services/postulacionService';
import { AccionPostulacion } from '../types/postulacion';

/** Solicitudes de un tema (solo el guía/coguía o el coordinador). */
export function usePostulaciones(temaId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['postulaciones', temaId],
    queryFn: () => postulacionService.list(temaId),
    enabled: enabled && Number.isFinite(temaId),
  });
}

/** Postulaciones pendientes que me toca resolver (soy guía/coguía del tema).
 *  Con `todas` (solo coordinador) trae las de todo el sistema; se cachea aparte
 *  de las propias, y la invalidación por prefijo refresca ambas. */
export function useMisPostulaciones(enabled = true, todas = false) {
  return useQuery({
    queryKey: ['mis-postulaciones', todas],
    queryFn: () => postulacionService.porResolver(todas),
    enabled,
  });
}

/** Resolver desde la pestaña "Solicitudes": refresca la lista propia, los temas y los procesos. */
export function useResolverMiPostulacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accion, motivo }: { id: number; accion: AccionPostulacion; motivo?: string }) =>
      postulacionService.resolver(id, accion, motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mis-postulaciones'] });
      qc.invalidateQueries({ queryKey: ['temas'] });
      qc.invalidateQueries({ queryKey: ['instancias'] });
    },
  });
}

/**
 * Resolver desde la ficha cambia el estado del tema y del resto de solicitudes, pero
 * también lo que cuentan el badge de la barra lateral (`mis-postulaciones`) y la
 * pestaña Solicitudes de Procesos: hay que invalidar las cuatro claves o quedan viejas.
 */
function useInvalidarTema(temaId: number) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['temas'] });
    qc.invalidateQueries({ queryKey: ['postulaciones', temaId] });
    qc.invalidateQueries({ queryKey: ['mis-postulaciones'] });
    qc.invalidateQueries({ queryKey: ['instancias'] });
  };
}

export function usePostular(temaId: number) {
  const invalidar = useInvalidarTema(temaId);
  return useMutation({
    mutationFn: (mensaje: string) => postulacionService.postular(temaId, mensaje),
    onSuccess: invalidar,
  });
}

export function useResolverPostulacion(temaId: number) {
  const invalidar = useInvalidarTema(temaId);
  return useMutation({
    mutationFn: ({ id, accion, motivo }: { id: number; accion: AccionPostulacion; motivo?: string }) =>
      postulacionService.resolver(id, accion, motivo),
    onSuccess: invalidar,
  });
}

/** El estudiante retira su postulación pendiente; refresca el tema. */
export function useRetirarPostulacion(temaId: number) {
  const invalidar = useInvalidarTema(temaId);
  return useMutation({
    mutationFn: (id: number) => postulacionService.retirar(id),
    onSuccess: invalidar,
  });
}
